import React, { useEffect, useMemo, useState } from "react";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogClose,
} from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import RHFTextArea from "@/components/form/RHFTextArea";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";

import {
    deleteTenantReasonSchema,
    deleteTenantReasonDefaultValues,
} from "../validation.schema";
import { TenantService } from "@/services/tenantService";
import { X } from "lucide-react";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

const DeleteTenantModal = ({
    open,
    onClose,
    tenantId,
    loading = false,
    setSilentFetch,
}) => {
    const [submitting, setSubmitting] = useState(false);

    // react-hook-form setup
    const methods = useForm({
        resolver: zodResolver(deleteTenantReasonSchema),
        mode: "onBlur",
        defaultValues: deleteTenantReasonDefaultValues,
    });

    const { handleSubmit, reset } = methods;

    // reset form when modal opens
    useEffect(() => {
        if (!open) return;
        reset(deleteTenantReasonDefaultValues);
    }, [open, reset]);

    // combined loading state (API + local submit)
    const effectiveLoading = useMemo(
        () => loading || submitting,
        [loading, submitting]
    );

    // whether delete is allowed
    const canDelete = useMemo(
        () => !effectiveLoading,
        [effectiveLoading]
    );

    // submit delete request
    const submitHandler = async (formData) => {
        if (!tenantId) return;
        if (!canDelete) return;
        if (submitting) return;

        setSubmitting(true);

        try {
            await TenantService.deleteTenant(
                tenantId,
                formData.reason.trim()
            );

            toast.success("Tenant deleted");

            onClose?.();
        } catch (e) {
            console.error(e);
            toast.error(apiErrorMessageFormat(e, "Failed to delete tenant"));
        } finally {
            setSubmitting(false);
            setSilentFetch(true);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) onClose?.();
            }}
        >
            <DialogContent className="w-[520px] md:w-full flex flex-col p-0 overflow-y-auto scrollbar-hide">

                {/* Header */}
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <div>
                        <DialogTitle className="text-[15px] font-semibold text-text-primary">
                            Delete Tenant
                        </DialogTitle>

                        <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
                            This action cannot be undone. Please provide a reason before deleting this tenant.
                        </DialogDescription>
                    </div>

                    {/* Close button */}
                    <DialogClose className="cursor-pointer absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center text-text-primary hover:bg-background-icon/60 transition-colors">
                        <span className="w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center text-text-primary hover:bg-background-icon/60 transition-colors">
                            <X size={16} strokeWidth={3} />
                        </span>
                    </DialogClose>
                </DialogHeader>

                {/* Form section */}
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(submitHandler)}>
                        <div className="px-5 py-4">

                            {/* Reason input */}
                            <RHFTextArea
                                name="reason"
                                label="Reason"
                                placeholder="Enter reason for deletion..."
                                required
                                rows={5}
                                maxLength={500}
                            />

                            {/* Actions */}
                            <div className="flex justify-end gap-3 mt-6">
                                <DialogClose
                                    render={
                                        <Button
                                            variant="outline"
                                            className="bg-button-tertiary border-none"
                                            type="button"
                                            disabled={effectiveLoading}
                                        >
                                            Cancel
                                        </Button>
                                    }
                                />

                                <Button
                                    className="bg-destructive text-white"
                                    type="submit"
                                    disabled={!canDelete}
                                >
                                    {effectiveLoading ? "Deleting…" : "Delete Tenant"}
                                </Button>
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};

export default DeleteTenantModal;